import { NodeWithPos } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { LIST_TYPE_ORDERED } from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { ATTENDEE_LIST_NODE_NAME } from '../../../../../../backend/src/tiptap/editor/extensions/nodes/attendee-list'
import { MINUTE_TITLE_NODE_NAME } from '../../../../../../backend/src/tiptap/editor/extensions/nodes/minute-title'
import { placeChildren } from './locked-items-plugin'
import { isMinuteTree } from './minute-tree-plugin'

/**
 * Says of each block at the top of the document whether the export will read it.
 *
 * The export takes three things: the title, the attendee list, and one body list. Anything else a reader writes
 * at that level stays in the editor and never reaches the exported minutes, which is worth saying out loud
 * rather than leaving them to find out afterwards.
 *
 * The labels draw a line between the two, so they are only worth drawing when something falls on the far side
 * of it. A document where everything is exported — the usual one — is left unframed rather than boxed up to
 * make a point it does not need to make.
 *
 * Every label comes from here rather than from a stylesheet selector, including the two that say a block is
 * exported. Which list counts as the body is not something a selector can decide: the export prefers the
 * agenda, but a meeting whose minutes have not been generated is shown its outline instead, and the export falls
 * back to the first list it finds. Move a label back into CSS and that list — the body of the document, in its
 * most common state — is the one block left saying nothing.
 */

const EXPORTED_ATTENDEES_CLASS = 'exported-attendees'
const EXPORTED_CONTENT_CLASS = 'exported-content'
const NOT_EXPORTED_CLASS = 'not-exported'

const exportScopePluginKey = new PluginKey<DecorationSet>('exportScope')

/**
 * The list the export reads as the body: the agenda if the document has one, otherwise the first list.
 *
 * Mirrors `selectBodyList` in `backend/src/meetings/prosemirror-export/prosemirror-to-xml.ts`.
 */
function findExportedBodyList(blocks: NodeWithPos[]): NodeWithPos | null {
  const lists = blocks.filter(({ node }) => node.type.name === LIST_TYPE_ORDERED)
  return lists.find(({ node }) => isMinuteTree(node)) ?? lists[0] ?? null
}

/**
 * What to say about a block, or nothing if it should be left unframed.
 *
 * The title is exported but goes unlabelled on purpose: it already reads unmistakably as the document's own
 * title, and a legend around a heading would only be in the way. An empty block is left alone too — it holds
 * nothing to lose, and framing the blank line a reader is about to type into helps nobody.
 */
function classifyBlock(block: NodeWithPos, bodyList: NodeWithPos | null): string | null {
  if (block.node.type.name === MINUTE_TITLE_NODE_NAME) {
    return null
  }
  if (block.node.type.name === ATTENDEE_LIST_NODE_NAME) {
    return EXPORTED_ATTENDEES_CLASS
  }
  if (block.pos === bodyList?.pos) {
    return EXPORTED_CONTENT_CLASS
  }
  return block.node.content.size > 0 ? NOT_EXPORTED_CLASS : null
}

export type ClassifiedBlock = NodeWithPos & { exportStatus: string }

export function classifyBlocks(doc: ProseMirrorNode): ClassifiedBlock[] {
  const blocks = placeChildren(doc, 0)
  const bodyList = findExportedBodyList(blocks)
  return blocks.flatMap((block) => {
    const exportStatus = classifyBlock(block, bodyList)
    return exportStatus === null ? [] : [{ ...block, exportStatus }]
  })
}

export function createExportScopePlugin(): Plugin<DecorationSet> {
  function build(doc: ProseMirrorNode): DecorationSet {
    const blocks = classifyBlocks(doc)
    if (!blocks.some(({ exportStatus }) => exportStatus === NOT_EXPORTED_CLASS)) {
      return DecorationSet.empty
    }
    return DecorationSet.create(
      doc,
      blocks.map(({ node, pos, exportStatus }) => Decoration.node(pos, pos + node.nodeSize, { class: exportStatus }))
    )
  }

  return new Plugin<DecorationSet>({
    key: exportScopePluginKey,

    state: {
      init(_, state) {
        return build(state.doc)
      },
      apply(transaction, decorations, _oldState, newState) {
        return transaction.docChanged ? build(newState.doc) : decorations
      },
    },

    props: {
      decorations(state) {
        return exportScopePluginKey.getState(state) ?? DecorationSet.empty
      },
    },
  })
}
